import React, { useCallback } from 'react'
import { AsyncSelect, Field } from '@workwhile/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  fetchFuzzySearchPositions,
  positionsKeys,
  usePositionByIdQuery,
} from 'queries/positions'

type PositionOption = { label: string; value: number }

type PositionPickerProps = {
  value?: number | null
  onChange: (value: number | undefined) => void
  error?: string
  label?: React.ReactNode
}

const toOption = (position: {
  id: number
  name: string
  companyName?: string
}): PositionOption => ({
  label: position.companyName
    ? `${position.name} — ${position.companyName} (${position.id})`
    : `${position.name} (${position.id})`,
  value: position.id,
})

export const PositionPicker = ({
  value,
  onChange,
  error,
  label = 'Position',
}: PositionPickerProps) => {
  const queryClient = useQueryClient()
  const { data: selectedPosition } = usePositionByIdQuery(value)

  const loadOptions = useCallback(
    async (rawInput: string) => {
      const search = rawInput.trim()
      if (!search) {
        return []
      }

      const positions = await queryClient.fetchQuery({
        queryKey: positionsKeys.fuzzySearch({ search, limit: 20 }),
        queryFn: () => fetchFuzzySearchPositions(search, 20),
      })

      return positions.map((position) =>
        toOption({
          id: position.id,
          name: position.name,
          companyName: position.companyName,
        })
      )
    },
    [queryClient]
  )

  const selectedOption: PositionOption | null = selectedPosition
    ? toOption({
        id: selectedPosition.id,
        name: selectedPosition.name,
        companyName: selectedPosition.company.name,
      })
    : null

  return (
    <Field label={label} error={error}>
      <AsyncSelect
        block
        isClearable
        isSearchable
        placeholder="Search by name or ID"
        loadOptions={loadOptions}
        cacheOptions
        menuPortalTarget={document.body}
        value={selectedOption}
        onChange={(option) => {
          onChange(option?.value ?? undefined)
        }}
        menuPosition="fixed"
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 10011,
            pointerEvents: 'auto',
          }),
        }}
        noOptionsMessage={() =>
          value ? 'No matching positions' : 'Type to search positions'
        }
      />
    </Field>
  )
}
